import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  PaperPlaneTilt, PencilSimple, Trash, ArrowsClockwise, StopCircle, Flask, X,
} from "@phosphor-icons/react";

const ALL = "__all__";

const STATUS_META = {
  draft: { label: "Entwurf", cls: "bg-zinc-100 text-zinc-600 border border-zinc-200" },
  queued: { label: "Wartend", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  sending: { label: "Wird gesendet…", cls: "bg-blue-50 text-[#002FA7] border border-[#002FA7]/20" },
  sent: { label: "Gesendet", cls: "bg-[#00796B]/10 text-[#00796B] border border-[#00796B]/20" },
  error: { label: "Fehler", cls: "bg-red-50 text-[#E53935] border border-red-200" },
};

const fmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function EmailVersand() {
  const [items, setItems] = useState([]);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fStatus, setFStatus] = useState(ALL);
  const [selected, setSelected] = useState(new Set());
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [sendDialog, setSendDialog] = useState(false);
  const [interval_, setInterval_] = useState(45);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [q, j] = await Promise.all([api.get("/email-queue"), api.get("/email-queue/send-job")]);
      setItems(q.data);
      setJob(j.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live refresh while a send job is running.
  useEffect(() => {
    if (job?.status !== "running") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [job?.status, load]);

  const filtered = useMemo(
    () => items.filter((i) => fStatus === ALL || i.status === fStatus),
    [items, fStatus]
  );

  const draftIds = useMemo(() => {
    const drafts = filtered.filter((i) => i.status === "draft");
    if (selected.size > 0) return drafts.filter((i) => selected.has(i.id)).map((i) => i.id);
    return drafts.map((i) => i.id);
  }, [filtered, selected]);

  const counts = useMemo(() => {
    const c = { draft: 0, queued: 0, sending: 0, sent: 0, error: 0 };
    items.forEach((i) => { if (c[i.status] !== undefined) c[i.status]++; });
    return c;
  }, [items]);

  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleAll = () => {
    const ids = filtered.map((i) => i.id);
    const allSel = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected(allSel ? new Set() : new Set(ids));
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/email-queue/${edit.id}`, {
        to_email: edit.to_email, subject: edit.subject, body: edit.body,
      });
      setItems((is) => is.map((i) => (i.id === data.id ? data : i)));
      setEdit(null);
      toast.success("E-Mail gespeichert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const testSend = async (item) => {
    setTestingId(item.id);
    try {
      const { data } = await api.post(`/email-queue/${item.id}/test`);
      toast.success(`Test-E-Mail an ${data.to} gesendet`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test-Versand fehlgeschlagen");
    } finally {
      setTestingId(null);
    }
  };

  const removeItem = async (item) => {
    await api.delete(`/email-queue/${item.id}`);
    setItems((is) => is.filter((i) => i.id !== item.id));
    setSelected((prev) => { const n = new Set(prev); n.delete(item.id); return n; });
    toast.success("E-Mail gelöscht");
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    const { data } = await api.post("/email-queue/delete-bulk", Array.from(selected));
    setSelected(new Set());
    await load();
    toast.success(`${data.deleted} E-Mails gelöscht`);
  };

  const startSend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/email-queue/send", {
        ids: draftIds,
        interval_seconds: Number(interval_) || 45,
      });
      setSendDialog(false);
      setSelected(new Set());
      toast.success(`Versand gestartet: ${data.total} E-Mails, alle ${data.interval_seconds} Sekunden`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Versand konnte nicht gestartet werden");
    } finally {
      setBusy(false);
    }
  };

  const cancelJob = async () => {
    try {
      await api.post(`/email-queue/send-jobs/${job.id}/cancel`);
      toast.info("Versand wird gestoppt…");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((i) => selected.has(i.id));

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">E-Mail Versand</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {counts.draft} Entwürfe · {counts.queued + counts.sending} im Versand · {counts.sent} gesendet · {counts.error} Fehler
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={deleteSelected} disabled={selected.size === 0} data-testid="delete-selected-emails-button" className="rounded-sm border-zinc-300 text-[#E53935] hover:text-[#E53935]">
            <Trash size={16} className="mr-2" /> Ausgewählte löschen
          </Button>
          <Button onClick={() => setSendDialog(true)} disabled={draftIds.length === 0 || job?.status === "running"} data-testid="open-send-dialog-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
            <PaperPlaneTilt size={16} className="mr-2" /> Versand starten ({draftIds.length})
          </Button>
        </div>
      </div>

      {/* Running send job */}
      {job?.status === "running" && (
        <div className="bg-white border border-[#002FA7]/30 rounded-md p-4 mb-5" data-testid="send-job-progress">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="font-medium text-zinc-700">
              Versand läuft… Intervall: {job.interval_seconds}s
              {job.failed > 0 && <span className="text-[#E53935]"> · {job.failed} Fehler</span>}
            </span>
            <div className="flex items-center gap-3">
              <span className="text-zinc-500 tabular-nums" data-testid="send-job-count">{job.done} / {job.total}</span>
              <Button size="sm" variant="outline" onClick={cancelJob} data-testid="cancel-send-job-button" className="h-7 text-xs rounded-sm border-zinc-300 text-[#E53935] hover:text-[#E53935]">
                <StopCircle size={14} className="mr-1" /> Stoppen
              </Button>
            </div>
          </div>
          <Progress value={job.total > 0 ? (job.done / job.total) * 100 : 0} className="h-2" />
        </div>
      )}
      {job && job.status !== "running" && job.status !== "done" && (
        <div className="text-xs text-zinc-500 mb-5" data-testid="last-job-info">
          Letzter Versand: {job.status === "cancelled" ? "gestoppt" : job.status} · {job.sent} gesendet, {job.failed} Fehler
        </div>
      )}

      {/* Filter */}
      <div className="bg-white border border-zinc-200 rounded-md p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="email-filter-bar">
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-44 h-9 rounded-sm" data-testid="filter-email-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Alle Status</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {fStatus !== ALL && (
          <Button variant="ghost" size="sm" onClick={() => setFStatus(ALL)} className="h-9 text-xs">
            <X size={14} className="mr-1" /> Zurücksetzen
          </Button>
        )}
        <span className="text-xs text-zinc-400 ml-auto">{filtered.length} von {items.length} angezeigt · {selected.size} ausgewählt</span>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 py-12 text-center">Lade…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-md p-12 text-center text-zinc-500" data-testid="email-queue-empty">
          Noch keine E-Mails generiert. Wähle unter „Leads" Empfänger aus und klicke „E-Mails generieren".
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto" data-testid="email-queue-table">
          <table className="w-full text-sm border-collapse min-w-[1100px]">
            <thead>
              <tr className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-3 w-10">
                  <Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll} data-testid="select-all-emails-checkbox" />
                </th>
                <th className="px-3 py-3 font-medium">Lead</th>
                <th className="px-3 py-3 font-medium">An</th>
                <th className="px-3 py-3 font-medium">Betreff</th>
                <th className="px-3 py-3 font-medium">Postfach</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium">Erstellt</th>
                <th className="px-3 py-3 font-medium text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} data-testid="email-row" className={`border-t border-zinc-100 hover:bg-zinc-50 ${selected.has(i.id) ? "bg-[#002FA7]/5" : ""}`}>
                  <td className="px-3 py-2.5">
                    <Checkbox checked={selected.has(i.id)} onCheckedChange={() => toggle(i.id)} data-testid={`email-checkbox-${i.id}`} />
                  </td>
                  <td className="px-3 py-2.5 font-medium max-w-[200px]">
                    <div className="truncate" title={i.lead_name}>{i.lead_name}</div>
                    <div className="text-[10px] text-zinc-400">#{String(i.lead_no ?? "").padStart(4, "0")}</div>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-600 max-w-[200px]"><div className="truncate" title={i.to_email}>{i.to_email}</div></td>
                  <td className="px-3 py-2.5 max-w-[280px]">
                    <button onClick={() => setEdit({ ...i })} className="truncate block text-left hover:text-[#002FA7] hover:underline w-full" title={i.subject} data-testid={`open-email-${i.id}`}>
                      {i.subject}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-600 text-xs max-w-[160px]"><div className="truncate">{i.mailbox_email}</div></td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[11px] px-2 py-1 rounded-sm whitespace-nowrap ${STATUS_META[i.status]?.cls || ""}`} title={i.error || ""} data-testid={`email-status-${i.id}`}>
                      {STATUS_META[i.status]?.label || i.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-500 text-xs whitespace-nowrap">
                    {i.status === "sent" && i.sent_at ? <>Gesendet: {fmt(i.sent_at)}</> : fmt(i.created_at)}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEdit({ ...i })} disabled={i.status === "queued" || i.status === "sending"} data-testid={`edit-email-${i.id}`} className="h-8 text-xs">
                        <PencilSimple size={15} className="mr-1" /> Bearbeiten
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => testSend(i)} disabled={testingId === i.id} data-testid={`test-email-${i.id}`} className="h-8 text-xs" title="Test-Mail an das eigene Postfach senden">
                        {testingId === i.id ? <ArrowsClockwise size={15} className="mr-1 animate-spin" /> : <Flask size={15} className="mr-1" />}
                        Test
                      </Button>
                      <button onClick={() => removeItem(i)} disabled={i.status === "queued" || i.status === "sending"} data-testid={`delete-email-${i.id}`} className="ml-1 text-zinc-300 hover:text-[#E53935] disabled:opacity-30">
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-400">Keine E-Mails mit diesem Status.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl" data-testid="email-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">E-Mail bearbeiten</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-zinc-600">Empfänger</Label>
                  <Input type="email" value={edit.to_email} onChange={(e) => setEdit((x) => ({ ...x, to_email: e.target.value }))} className="mt-1 h-9" data-testid="edit-email-to" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-600">Lead</Label>
                  <div className="mt-1 h-9 flex items-center text-sm text-zinc-600 truncate">{edit.lead_name}</div>
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-600">Betreff</Label>
                <Input value={edit.subject} onChange={(e) => setEdit((x) => ({ ...x, subject: e.target.value }))} className="mt-1 h-9" data-testid="edit-email-subject" />
              </div>
              <div>
                <Label className="text-xs text-zinc-600">Text</Label>
                <Textarea value={edit.body} onChange={(e) => setEdit((x) => ({ ...x, body: e.target.value }))} rows={12} className="mt-1 text-sm" data-testid="edit-email-body" />
                <p className="text-xs text-zinc-400 mt-1">Die Signatur des Postfachs ({edit.mailbox_email}) wird beim Versand automatisch angehängt.</p>
              </div>
              {edit.error && (
                <div className="text-xs text-[#E53935] bg-red-50 border border-red-200 rounded-sm p-2">Letzter Fehler: {edit.error}</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="save-email-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
              {saving && <ArrowsClockwise size={16} className="mr-2 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={sendDialog} onOpenChange={setSendDialog}>
        <DialogContent data-testid="send-dialog">
          <DialogHeader><DialogTitle className="font-heading">Versand starten</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-500" data-testid="send-dialog-info">
              {draftIds.length} {selected.size > 0 ? "ausgewählte Entwürfe" : "Entwürfe"} werden nacheinander über einen Hintergrundprozess versendet.
              Der Lead-Status wechselt dabei auf <span className="font-medium">„E-mail Versand"</span>.
            </p>
            <div>
              <Label className="text-xs text-zinc-600">Intervall zwischen E-Mails (Sekunden)</Label>
              <Input type="number" min={5} max={600} value={interval_} onChange={(e) => setInterval_(e.target.value)} className="mt-1 h-9 w-32" data-testid="send-interval-input" />
              <p className="text-xs text-zinc-400 mt-1">Empfohlen: 30–60 Sekunden, um Spam-Filter zu vermeiden.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={startSend} disabled={busy} data-testid="start-send-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
              {busy ? <ArrowsClockwise size={16} className="mr-2 animate-spin" /> : <PaperPlaneTilt size={16} className="mr-2" />}
              {draftIds.length} E-Mails versenden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
